import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Download, RefreshCw, Database, DollarSign } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface B2BSale {
  id: string;
  sale_date: string;
  product_name: string;
  measurement: string | null;
  quantity_sold: number | null;
  selling_price: number | null;
  total_revenue: number | null;
  sync_date: string | null;
}

const B2BSalesData = () => {
  const [startDate, setStartDate] = useState<Date>(new Date('2024-05-01'));
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [selectedProduct, setSelectedProduct] = useState<string>('all');
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncStatus, setSyncStatus] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch B2B sales data filtered by date range
  const { data: salesData, isLoading: isDataLoading } = useQuery({
    queryKey: ['b2b-sales-data', startDate, endDate],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('b2b_sales_data')
        .select('*')
        .gte('sale_date', format(startDate, 'yyyy-MM-dd'))
        .lte('sale_date', format(endDate, 'yyyy-MM-dd'))
        .order('sale_date', { ascending: false })
        .limit(10000);
      if (error) throw error;
      return data as B2BSale[];
    },
  });

  // All-time stats
  const { data: allTimeStats } = useQuery({
    queryKey: ['b2b-sales-all-time-stats'],
    queryFn: async () => {
      const { count, error: countError } = await supabase
        .from('b2b_sales_data')
        .select('*', { count: 'exact', head: true });
      if (countError) throw countError;

      const { data, error } = await supabase
        .from('b2b_sales_data')
        .select('product_name, quantity_sold, selling_price, total_revenue')
        .limit(10000);
      if (error) throw error;

      const totalSold = (data || []).reduce((s, r) => s + (r.quantity_sold || 0), 0);
      const totalRevenue = (data || []).reduce((s, r) => s + (r.total_revenue || 0), 0);
      const uniqueProducts = new Set((data || []).map(r => r.product_name)).size;

      return {
        totalRecords: count || 0,
        totalSold,
        totalRevenue,
        uniqueProducts,
      };
    },
  });

  const handleSync = async () => {
    setIsSyncing(true);
    setSyncProgress(30);
    setSyncStatus('Syncing B2B sales data...');

    try {
      const { data, error } = await supabase.functions.invoke('b2b-sales-sync', {
        body: {
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd'),
        },
      });

      if (error) throw error;

      setSyncProgress(100);
      setSyncStatus('Sync completed!');

      setTimeout(() => {
        setIsSyncing(false);
        setSyncProgress(0);
        setSyncStatus('');
      }, 1000);

      toast({
        title: 'Sync Completed',
        description: `Successfully synced ${data?.recordsProcessed || 0} B2B sales records`,
      });
      queryClient.invalidateQueries({ queryKey: ['b2b-sales-data'] });
      queryClient.invalidateQueries({ queryKey: ['b2b-sales-all-time-stats'] });
    } catch (error: any) {
      setIsSyncing(false);
      setSyncProgress(0);
      setSyncStatus('');
      toast({
        title: 'Sync Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const exportToCSV = () => {
    if (!displayData?.length) return;
    const csvContent = [
      ['Product Name', 'Sale Date', 'Measurement', 'Qty Sold', 'Selling Price', 'Total Revenue', 'Sync Date'],
      ...displayData.map(row => [
        row.product_name,
        row.sale_date,
        row.measurement || '',
        row.quantity_sold || 0,
        row.selling_price || 0,
        row.total_revenue || 0,
        row.sync_date || '',
      ]),
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `b2b-sales-data-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const displayData = salesData?.filter(
    r => selectedProduct === 'all' || r.product_name === selectedProduct
  );

  const filteredSummary = displayData ? {
    totalRecords: displayData.length,
    totalSold: displayData.reduce((s, r) => s + (r.quantity_sold || 0), 0),
    totalRevenue: displayData.reduce((s, r) => s + (r.total_revenue || 0), 0),
    uniqueProducts: new Set(displayData.map(r => r.product_name)).size,
  } : null;

  const availableProducts = salesData
    ? Array.from(new Set(salesData.map(r => r.product_name))).sort()
    : [];

  const fmtNum = (n: number) => n.toLocaleString();
  const fmtCurrency = (n: number) => `ETB ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">B2B Sales Data</h1>
            <p className="text-muted-foreground">Sync and view sales data from B2B platform</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={exportToCSV} variant="outline" disabled={!displayData?.length}>
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* All-Time Stats */}
        {allTimeStats && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">All-Time Statistics</h2>
              <Badge variant="secondary">Database Total</Badge>
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Records</CardTitle>
                  <Database className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{fmtNum(allTimeStats.totalRecords)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Total Qty Sold</CardTitle></CardHeader>
                <CardContent><div className="text-2xl font-bold">{fmtNum(allTimeStats.totalSold)}</div></CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Unique Products</CardTitle></CardHeader>
                <CardContent><div className="text-2xl font-bold">{allTimeStats.uniqueProducts}</div></CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{fmtCurrency(allTimeStats.totalRevenue)}</div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Filtered Stats */}
        {filteredSummary && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">Filtered Results</h2>
              <Badge variant="outline">
                {format(startDate, 'MMM dd, yyyy')} - {format(endDate, 'MMM dd, yyyy')}
              </Badge>
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Records</CardTitle></CardHeader>
                <CardContent><div className="text-2xl font-bold">{fmtNum(filteredSummary.totalRecords)}</div></CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Qty Sold</CardTitle></CardHeader>
                <CardContent><div className="text-2xl font-bold">{fmtNum(filteredSummary.totalSold)}</div></CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Products</CardTitle></CardHeader>
                <CardContent><div className="text-2xl font-bold">{filteredSummary.uniqueProducts}</div></CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{fmtCurrency(filteredSummary.totalRevenue)}</div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Product Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-muted-foreground">Filter by Product:</label>
          <Select value={selectedProduct} onValueChange={setSelectedProduct}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="All Products" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Products</SelectItem>
              {availableProducts.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Sync Controls */}
        <Card>
          <CardHeader>
            <CardTitle>Data Synchronization</CardTitle>
            <CardDescription>Sync sales data from B2B platform</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4 items-center">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-[240px] justify-start text-left font-normal", !startDate && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, 'PPP') : 'Start date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={startDate} onSelect={(d) => d && setStartDate(d)} initialFocus className="pointer-events-auto" />
                </PopoverContent>
              </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-[240px] justify-start text-left font-normal", !endDate && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, 'PPP') : 'End date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={endDate} onSelect={(d) => d && setEndDate(d)} initialFocus className="pointer-events-auto" />
                </PopoverContent>
              </Popover>

              <Button onClick={handleSync} disabled={isSyncing}>
                <RefreshCw className={cn("mr-2 h-4 w-4", isSyncing && "animate-spin")} />
                {isSyncing ? 'Syncing...' : 'Sync Date Range'}
              </Button>
            </div>

            {isSyncing && (
              <div className="space-y-2">
                <Progress value={syncProgress} />
                <p className="text-sm text-muted-foreground">{syncStatus}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card>
          <CardHeader>
            <CardTitle>Sales Records</CardTitle>
            <CardDescription>
              {isDataLoading ? 'Loading...' : `${displayData?.length || 0} records`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-auto max-h-[600px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Measurement</TableHead>
                    <TableHead className="text-right">Qty Sold</TableHead>
                    <TableHead className="text-right">Selling Price</TableHead>
                    <TableHead className="text-right">Total Revenue</TableHead>
                    <TableHead>Sync Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayData?.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">{row.product_name}</TableCell>
                      <TableCell>{row.sale_date}</TableCell>
                      <TableCell>{row.measurement || '-'}</TableCell>
                      <TableCell className="text-right">{row.quantity_sold ?? 0}</TableCell>
                      <TableCell className="text-right">{fmtCurrency(row.selling_price ?? 0)}</TableCell>
                      <TableCell className="text-right font-medium">{fmtCurrency(row.total_revenue ?? 0)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {row.sync_date ? format(new Date(row.sync_date), 'MMM dd, HH:mm') : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!displayData || displayData.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={7} className="h-24 text-center">
                        No sales data found. Use the sync controls above to fetch data.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default B2BSalesData;
